'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useId, createContext, useContext } from 'react';
import { cn } from '../../utils/cn';
import { focusNextOrSave } from '../../utils/enter-navigation';
import { useTheme } from '../../contexts/theme-context';
import { Select } from '../select';
import { useMarkFormDirty } from './form-dirty-context';
import { useDateTextInput } from '../common/date-field';
import { useTimeTextInput } from '../common/time-field';
const AdaptiveFormContext = createContext(null);
export function AdaptiveForm({ children, onSubmit, layout = 'vertical', fieldSize = 'medium', className = '', id, enableKeyboardNavigation = true, gap = 'medium' }) {
    const formRef = useRef(null);
    const { theme } = useTheme();
    // Use direct CSS variable checking as fallback + theme context
    const [density, setDensity] = useState(() => {
        if (typeof window !== 'undefined') {
            const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--layout-density');
            return cssVar.trim() === 'compact' ? 'compact' : 'standard';
        }
        return theme?.layoutDensity === 'compact' ? 'compact' : 'standard';
    });
    const [forceUpdate, setForceUpdate] = useState(0);
    // Watch for CSS variable changes (more reliable than theme context)
    useEffect(() => {
        const checkDensity = () => {
            if (typeof window === 'undefined')
                return;
            const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--layout-density');
            const cssVarDensity = cssVar.trim() === 'compact' ? 'compact' : 'standard';
            const themeDensity = theme?.layoutDensity === 'compact' ? 'compact' : 'standard';
            // Use CSS variable as primary source, theme as fallback
            const newDensity = cssVar ? cssVarDensity : themeDensity;
            if (newDensity !== density) {
                setDensity(newDensity);
                setForceUpdate(prev => prev + 1);
            }
        };
        // Check immediately
        checkDensity();
        // Set up mutation observer to watch for CSS variable changes
        const observer = new MutationObserver(() => {
            setTimeout(checkDensity, 100); // Slight delay to allow CSS to update
        });
        if (document.documentElement) {
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['style', 'class']
            });
        }
        return () => observer.disconnect();
    }, [theme, density]);
    // Get all navigable (enabled, visible) fields in the form — skips hidden and disabled fields
    const getNavigableFields = () => {
        const form = formRef.current;
        if (!form)
            return [];
        return Array.from(form.querySelectorAll('input, select, textarea, [role="combobox"]')).filter(el => {
            // Skip hidden inputs (React Select renders a hidden <input> for its value)
            if (el.type === 'hidden')
                return false;
            // Skip disabled fields
            if (el.disabled)
                return false;
            // Skip combobox inside a disabled React Select (aria-disabled="true")
            if (el.getAttribute('role') === 'combobox' && el.getAttribute('aria-disabled') === 'true')
                return false;
            return true;
        });
    };
    // Handle keyboard navigation
    const handleFieldKeyDown = (e, _fieldIndex) => {
        if (!enableKeyboardNavigation)
            return;
        const allFields = getNavigableFields();
        if (!allFields.length)
            return;
        const currentEl = e.currentTarget;
        const currentIdx = allFields.indexOf(currentEl);
        if (currentIdx === -1)
            return;
        const totalFields = allFields.length;
        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                // Next field; after the last one, the Save button (Enter there saves)
                focusNextOrSave(currentEl, allFields);
                break;
            case 'ArrowDown':
                if (layout === 'horizontal' || layout === 'vertical') {
                    e.preventDefault();
                    if (currentIdx < totalFields - 1) {
                        allFields[currentIdx + 1]?.focus();
                    }
                }
                break;
            case 'ArrowUp':
                if (layout === 'horizontal' || layout === 'vertical') {
                    e.preventDefault();
                    if (currentIdx > 0) {
                        allFields[currentIdx - 1]?.focus();
                    }
                }
                break;
            case 'ArrowRight':
                if (layout === 'inline' && currentIdx < totalFields - 1) {
                    e.preventDefault();
                    allFields[currentIdx + 1]?.focus();
                }
                break;
            case 'ArrowLeft':
                if (layout === 'inline' && currentIdx > 0) {
                    e.preventDefault();
                    allFields[currentIdx - 1]?.focus();
                }
                break;
        }
    };
    // Get layout-specific classes
    const getLayoutClasses = () => {
        const gapClasses = {
            none: 'gap-0',
            small: density === 'compact' ? 'gap-1' : 'gap-2',
            medium: density === 'compact' ? 'gap-2' : 'gap-4',
            large: density === 'compact' ? 'gap-3' : 'gap-6'
        };
        let layoutClasses = '';
        switch (layout) {
            case 'single-column':
                layoutClasses = `flex flex-col ${gapClasses[gap]}`;
                break;
            case 'two-column':
                layoutClasses = `grid grid-cols-1 md:grid-cols-2 ${gapClasses[gap]}`;
                break;
            case 'horizontal':
                layoutClasses = `flex flex-col ${density === 'compact' ? 'gap-px' : 'gap-4'}`;
                break;
            case 'vertical':
                layoutClasses = `flex flex-col ${gapClasses[gap]}`;
                break;
            case 'inline':
                layoutClasses = `flex flex-wrap items-end ${gapClasses[gap]}`;
                break;
            case 'card-grid':
                layoutClasses = `grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${gapClasses[gap]}`;
                break;
            default:
                layoutClasses = `flex flex-col ${gapClasses[gap]}`;
        }
        return layoutClasses;
    };
    // Form-level Enter key handler for fields that don't call handleFieldKeyDown directly
    // (e.g. raw <input> elements, React Select dropdowns after selection)
    const handleFormKeyDown = (e) => {
        if (!enableKeyboardNavigation)
            return;
        if (e.key !== 'Enter')
            return;
        // Don't intercept Enter inside textareas (allow newlines)
        if (e.target.tagName === 'TEXTAREA')
            return;
        const allFields = getNavigableFields();
        if (!allFields.length)
            return;
        const currentEl = e.target;
        const currentIdx = allFields.indexOf(currentEl);
        if (currentIdx === -1)
            return;
        e.preventDefault();
        focusNextOrSave(currentEl, allFields);
    };
    // Focus the next field after the given element (used by AdaptiveSelectField after selection)
    const focusNextField = (currentElement) => {
        if (!enableKeyboardNavigation)
            return;
        const allFields = getNavigableFields();
        if (!allFields.length)
            return;
        const currentIdx = allFields.indexOf(currentElement);
        if (currentIdx === -1 || currentIdx >= allFields.length - 1)
            return;
        // Small delay to let React Select close its menu first
        setTimeout(() => {
            allFields[currentIdx + 1]?.focus();
        }, 50);
    };
    const contextValue = {
        handleFieldKeyDown,
        focusNextField,
        formRef,
        layout,
        density,
        fieldSize
    };
    return (_jsx(AdaptiveFormContext.Provider, { value: contextValue, children: _jsx("form", { ref: formRef, onSubmit: onSubmit, onKeyDown: handleFormKeyDown, className: cn(getLayoutClasses(), className), id: id, noValidate: true, children: children }) }, `form-${density}-${layout}-${forceUpdate}`));
}
/**
 * AdaptiveField type="date" / "time": the shared dd-mm-yyyy box (DateField's) or 12-hour
 * hh:mm AM box (TimeField's) — same on every PC whatever its language — with AdaptiveField's
 * look, Enter handling and field index. The page's onChange still gets an event-like
 * { target: { value: 'yyyy-mm-dd' | 'HH:mm', name } }.
 */
function AdaptiveDateInput({ kind = 'date', inputRef, name, value, onChange, placeholder, className, onKeyDown, onFocus, onBlur, fieldIndex, required, disabled, showInvalid, a11yProps, ...rest }) {
    const emitValue = (v) => onChange?.({ target: { value: v, name } });
    const dateBox = useDateTextInput({
        value: kind === 'date' ? value : '',
        onValueChange: emitValue,
        minIso: typeof rest.min === 'string' ? rest.min : '',
        maxIso: typeof rest.max === 'string' ? rest.max : '',
        disabled,
        onBlur,
    });
    const timeBox = useTimeTextInput({ value: kind === 'time' ? value : '', onValueChange: emitValue, disabled, onBlur });
    const date = kind === 'date'
        ? dateBox
        : { inputProps: timeBox.inputProps, invalidMessage: timeBox.invalidMessage, calendar: timeBox.picker };
    const { min: _min, max: _max, ...inputRest } = rest;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "relative", children: [_jsx("input", { ref: inputRef, type: "text", name: name, placeholder: placeholder || (kind === 'date' ? 'dd-mm-yyyy' : 'hh:mm AM'), className: cn(className, kind === 'date' ? 'pl-9' : 'pr-9'), onFocus: onFocus, "data-field-index": fieldIndex, required: required, disabled: disabled, ...a11yProps, ...inputRest, ...date.inputProps, onKeyDown: (e) => { date.inputProps.onKeyDown(e); if (!e.defaultPrevented)
                            onKeyDown(e); } }), _jsx("div", { className: cn('absolute inset-y-0 flex items-center pointer-events-none', kind === 'date' ? 'left-0 pl-3' : 'right-0 pr-3'), children: date.calendar })] }), showInvalid && date.invalidMessage && (_jsx("p", { role: "alert", className: "mt-1 text-sm text-red-600 dark:text-red-400", children: date.invalidMessage }))] }));
}
export function AdaptiveField({ label, name, type = 'text', placeholder, required = false, value, onChange, fieldIndex, autoFocus = false, disabled = false, className = '', labelWidth, helperText, error, rows = 3, colSpan = 1, uppercase = false, ...props }) {
    const context = useContext(AdaptiveFormContext);
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    // Label ↔ input link (clicking the label focuses the field; screen readers read the label)
    const autoId = useId();
    const inputId = props.id || `field-${autoId}`;
    const messageId = `${inputId}-msg`;
    const a11yProps = {
        id: inputId,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error || helperText ? messageId : undefined,
    };
    if (!context) {
        throw new Error('AdaptiveField must be used within an AdaptiveForm');
    }
    const { handleFieldKeyDown, layout, density, fieldSize } = context;
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);
    const handleKeyDown = (e) => {
        handleFieldKeyDown(e, fieldIndex);
    };
    // Get field size classes based on density and fieldSize
    const getFieldSizeClasses = () => {
        if (density === 'compact') {
            return {
                input: 'h-10 px-2 text-xs',
                textarea: 'px-2 py-1 text-xs min-h-[2.5rem]',
                label: 'text-xs',
                helper: 'text-xs'
            };
        }
        const sizeMap = {
            small: {
                input: 'h-9 px-3 text-sm',
                textarea: 'px-3 py-2 text-sm min-h-[2.5rem]',
                label: 'text-sm',
                helper: 'text-sm'
            },
            medium: {
                input: 'h-10 px-3 text-sm',
                textarea: 'px-3 py-2 text-sm min-h-[3rem]',
                label: 'text-sm',
                helper: 'text-sm'
            },
            large: {
                input: 'h-12 px-4 text-base',
                textarea: 'px-4 py-3 text-base min-h-[4rem]',
                label: 'text-base',
                helper: 'text-sm'
            }
        };
        return sizeMap[fieldSize];
    };
    const sizeClasses = getFieldSizeClasses();
    // Get layout-specific wrapper classes
    const getWrapperClasses = () => {
        const baseClasses = colSpan === 2 ? 'col-span-2' : '';
        const spacingClass = 'space-y-1'; // Match Select component spacing
        switch (layout) {
            case 'horizontal':
                return `${baseClasses} flex flex-col md:flex-row md:items-start ${density === 'compact' ? 'mb-px pb-1' : 'mb-4'}`;
            case 'inline':
                return `${baseClasses} flex-1 min-w-0`;
            case 'card-grid':
                return `${baseClasses} ${spacingClass} p-4 border border-gray-200 dark:border-gray-700 rounded-lg`;
            default:
                return `${baseClasses} ${spacingClass}`;
        }
    };
    // Get label classes
    const getLabelClasses = () => {
        const baseClasses = `font-medium text-gray-700 dark:text-gray-300 transition-colors duration-150 ${sizeClasses.label}`;
        const focusClasses = isFocused ? 'text-primary-700 dark:text-primary-400' : '';
        switch (layout) {
            case 'horizontal':
                const width = labelWidth || (density === 'compact' ? 'w-32' : 'w-48');
                return `${width} ${baseClasses} ${focusClasses} text-right ${density === 'compact' ? 'pr-3 pt-1' : 'pr-4 pt-2'} flex-shrink-0`;
            case 'inline':
                return `${baseClasses} ${focusClasses} block`;
            default:
                return `${baseClasses} ${focusClasses} block`;
        }
    };
    // Get input classes
    const getInputClasses = () => {
        const sizeClass = type === 'textarea' ? sizeClasses.textarea : sizeClasses.input;
        const baseClasses = `w-full border rounded-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder:text-gray-400 dark:placeholder:text-gray-500 ${sizeClass}`;
        const stateClasses = cn(isFocused
            ? 'border-primary-500 ring-1 ring-primary-200 dark:ring-primary-800 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800', disabled && 'cursor-not-allowed disabled:bg-white dark:disabled:bg-gray-800 disabled:text-gray-900 dark:disabled:text-gray-100 disabled:opacity-100 disabled:border-gray-300 dark:disabled:border-gray-600', error && 'border-red-500 focus:border-red-500 focus:ring-red-200', !error && 'dark:text-gray-100', (type === 'date' || type === 'datetime-local' || type === 'time') && !disabled && 'cursor-pointer');
        const layoutClasses = '';
        const finalClasses = cn(baseClasses, stateClasses, layoutClasses, uppercase && 'uppercase', className);
        return finalClasses;
    };
    // Get field container width for horizontal layout
    const getFieldWidth = () => {
        if (layout === 'horizontal') {
            return 'flex-1 min-w-0';
        }
        return 'w-full';
    };
    const wrappedOnChange = (e) => {
        if (uppercase) {
            e.target.value = e.target.value.toUpperCase();
        }
        onChange?.(e);
    };
    return (_jsxs("div", { className: getWrapperClasses(), children: [_jsxs("label", { htmlFor: inputId, className: getLabelClasses(), children: [label, required && _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), _jsxs("div", { className: getFieldWidth(), children: [type === 'checkbox' ? (_jsxs("div", { className: "flex items-center", children: [_jsx("input", { ref: inputRef, type: "checkbox", name: name, checked: value || false, onChange: onChange, onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, disabled: disabled, className: "w-4 h-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:ring-2 bg-white dark:bg-gray-700 cursor-pointer", ...a11yProps, ...props }), helperText && (_jsx("span", { id: error ? undefined : messageId, className: `ml-2 text-gray-600 dark:text-gray-400 ${sizeClasses.helper}`, children: helperText }))] })) : type === 'textarea' ? (_jsx("textarea", { ref: inputRef, name: name, value: value || '', onChange: wrappedOnChange, placeholder: placeholder, rows: rows, className: getInputClasses(), onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, required: required, disabled: disabled, ...a11yProps, ...props })) : type === 'date' || type === 'time' ? (_jsx(AdaptiveDateInput, { kind: type, inputRef: inputRef, name: name, value: value, onChange: onChange, placeholder: placeholder, className: getInputClasses(), onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), fieldIndex: fieldIndex, required: required, disabled: disabled, showInvalid: !error, a11yProps: a11yProps, ...props })) : (_jsx("input", { ref: inputRef, type: type, name: name, value: value || '', onChange: wrappedOnChange, placeholder: placeholder, className: getInputClasses(), onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, required: required, disabled: disabled, ...a11yProps, ...props })), helperText && type !== 'checkbox' && (_jsx("p", { id: error ? undefined : messageId, className: `mt-1 text-gray-600 dark:text-gray-400 ${sizeClasses.helper}`, children: helperText })), error && (_jsx("p", { id: messageId, role: "alert", className: `mt-1 text-red-600 dark:text-red-400 ${sizeClasses.helper}`, children: error }))] })] }));
}
export function AdaptiveSelectField({ label, name: _name, options, value, onChange, fieldIndex: _fieldIndex, placeholder = 'Select...', required = false, disabled = false, isSearchable = false, isClearable = false, isMulti = false, isLoading = false, onInputChange, onMenuScrollToBottom, onMenuOpen, filterOption, noOptionsMessage, className = '', helperText, error, colSpan = 1, isCreatable = false, onCreateOption, formatCreateLabel, uppercase = false, singleRowValues = false }) {
    const context = useContext(AdaptiveFormContext);
    const markFormDirty = useMarkFormDirty();
    const [isFocused, setIsFocused] = useState(false);
    const selectContainerRef = useRef(null);
    // Label ↔ dropdown link (clicking the label focuses the dropdown)
    const selectInputId = `select-${useId()}`;
    if (!context) {
        throw new Error('AdaptiveSelectField must be used within an AdaptiveForm');
    }
    const { layout, density, fieldSize, focusNextField } = context;
    // After a single-select option is chosen, move focus to next field
    const handleChange = (option) => {
        // react-select fires no native change event, so tell the modal directly
        markFormDirty?.();
        onChange?.(option);
        if (!isMulti && option) {
            // Find the combobox element within this select to use as reference
            const combobox = selectContainerRef.current?.querySelector('[role="combobox"]');
            if (combobox) {
                focusNextField(combobox);
            }
        }
    };
    // Get field size classes
    const getFieldSizeClasses = () => {
        if (density === 'compact') {
            return {
                label: 'text-xs',
                helper: 'text-xs',
                control: {
                    minHeight: '40px',
                    height: '40px',
                    fontSize: '12px'
                }
            };
        }
        const sizeMap = {
            small: {
                label: 'text-sm',
                helper: 'text-sm',
                control: { minHeight: '36px', height: '36px', fontSize: '14px' }
            },
            medium: {
                label: 'text-sm',
                helper: 'text-sm',
                control: { minHeight: '40px', height: '40px', fontSize: '14px' }
            },
            large: {
                label: 'text-base',
                helper: 'text-sm',
                control: { minHeight: '48px', height: '48px', fontSize: '16px' }
            }
        };
        return sizeMap[fieldSize];
    };
    const sizeClasses = getFieldSizeClasses();
    // Get layout-specific wrapper classes
    const getWrapperClasses = () => {
        const baseClasses = colSpan === 2 ? 'col-span-2' : '';
        const spacingClass = 'space-y-1'; // Match Select component spacing
        switch (layout) {
            case 'horizontal':
                return `${baseClasses} flex flex-col md:flex-row md:items-start ${density === 'compact' ? 'mb-px pb-1' : 'mb-4'}`;
            case 'inline':
                return `${baseClasses} flex-1 min-w-0`;
            case 'card-grid':
                return `${baseClasses} ${spacingClass} p-4 border border-gray-200 dark:border-gray-700 rounded-lg`;
            default:
                return `${baseClasses} ${spacingClass}`;
        }
    };
    // Get label classes
    const getLabelClasses = () => {
        const baseClasses = `font-medium text-gray-700 dark:text-gray-300 transition-colors duration-150 ${sizeClasses.label}`;
        const focusClasses = isFocused ? 'text-primary-700 dark:text-primary-400' : '';
        switch (layout) {
            case 'horizontal':
                const width = density === 'compact' ? 'w-32' : 'w-48';
                return `${width} ${baseClasses} ${focusClasses} text-right ${density === 'compact' ? 'pr-3 py-1' : 'pr-4 py-2'} flex-shrink-0`;
            case 'inline':
                return `${baseClasses} ${focusClasses} block`;
            default:
                return `${baseClasses} ${focusClasses} block`;
        }
    };
    // Get field container width
    const getFieldWidth = () => {
        if (layout === 'horizontal') {
            return density === 'compact' ? 'w-full md:w-[35%]' : 'w-full md:flex-1';
        }
        return 'w-full';
    };
    return (_jsxs("div", { ref: selectContainerRef, className: getWrapperClasses(), children: [_jsxs("label", { htmlFor: selectInputId, className: getLabelClasses(), children: [label, required && _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), _jsxs("div", { className: getFieldWidth(), children: [_jsx(Select, { inputId: selectInputId, "aria-invalid": error ? true : undefined, placeholder: placeholder, options: options, value: value, onChange: handleChange, isLoading: isLoading, ...(isCreatable ? { isCreatable } : {}), ...(onCreateOption ? { onCreateOption: (value) => { markFormDirty?.(); onCreateOption(value); } } : {}), ...(formatCreateLabel ? { formatCreateLabel } : {}), ...(onInputChange ? { onInputChange } : {}), ...(onMenuScrollToBottom ? { onMenuScrollToBottom } : {}), ...(onMenuOpen ? { onMenuOpen } : {}), ...(filterOption !== undefined ? { filterOption } : {}), ...(noOptionsMessage ? { noOptionsMessage } : {}), ...(isMulti ? {
                            formatOptionLabel: (option, { context }) => {
                                if (context === 'value')
                                    return option.label;
                                const selected = Array.isArray(value) ? value.some(v => v.value === option.value) : false;
                                return (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx("input", { type: "checkbox", checked: selected, readOnly: true, style: { accentColor: '#3b82f6', width: '16px', height: '16px', cursor: 'pointer' } }), _jsx("span", { children: option.label })] }));
                            },
                        } : {}), className: cn('text-xs', density === 'compact' ? 'adaptive-select-compact' : 'adaptive-select-standard', className), classNamePrefix: "adaptive-select", styles: {
                            control: (base, state) => ({
                                ...base,
                                minHeight: sizeClasses.control.minHeight,
                                ...(isMulti ? {} : { height: sizeClasses.control.height }),
                                fontSize: sizeClasses.control.fontSize,
                                borderColor: error ? '#ef4444' : (state.isFocused ? '#3B82F6' : base.borderColor),
                                boxShadow: error ? 'none' : (state.isFocused ? '0 0 0 1px #3B82F6' : base.boxShadow),
                                ...(state.isDisabled ? {
                                    backgroundColor: '#ffffff',
                                    opacity: 1,
                                    cursor: 'not-allowed'
                                } : {}),
                                // Force override any default React Select heights
                                '&': {
                                    minHeight: `${sizeClasses.control.minHeight} !important`,
                                    ...(isMulti ? {} : { height: `${sizeClasses.control.height} !important` })
                                }
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                ...(isMulti ? { minHeight: sizeClasses.control.minHeight, flexWrap: (singleRowValues ? 'nowrap' : 'wrap'), ...(singleRowValues ? { minWidth: 0, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } } : {}) } : { height: sizeClasses.control.height, minHeight: sizeClasses.control.minHeight }),
                                padding: density === 'compact' ? '0 6px' : '0 12px',
                                // Single select keeps react-select's grid: value and search input share one cell
                                // (see input.gridArea). With flex + react-select's flexWrap a long value pushed the
                                // input to a second row and the value jumped to the top of the box.
                                display: isMulti ? 'flex' : 'grid',
                                alignItems: 'center'
                            }),
                            // One-row chips must not shrink (they scroll sideways instead)
                            multiValue: (base) => (singleRowValues ? { ...base, flexShrink: 0 } : base),
                            option: (base, state) => ({
                                ...base,
                                ...(isMulti ? {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    // In multi mode, selection is shown via the checkbox in
                                    // formatOptionLabel — don't let react-select's isSelected
                                    // styling kick in (blue bg + white text), which would make the
                                    // already-picked option's label invisible against the
                                    // transparent background.
                                    backgroundColor: state.isFocused ? '#f3f4f6' : 'transparent',
                                    color: '#111827',
                                } : {}),
                            }),
                            input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                                fontSize: sizeClasses.control.fontSize,
                                gridArea: '1 / 1 / 2 / 3',
                                ...(uppercase ? { textTransform: 'uppercase' } : {})
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: sizeClasses.control.height,
                                minHeight: sizeClasses.control.minHeight,
                                padding: density === 'compact' ? '0 4px' : '0 8px',
                                display: 'flex',
                                alignItems: 'center'
                            }),
                            indicatorSeparator: (base) => ({
                                ...base,
                                height: density === 'compact' ? '12px' : '20px',
                                marginTop: density === 'compact' ? '6px' : '10px'
                            }),
                            dropdownIndicator: (base) => ({
                                ...base,
                                padding: density === 'compact' ? '2px' : '8px',
                                fontSize: sizeClasses.control.fontSize
                            }),
                            placeholder: (base) => ({
                                ...base,
                                fontSize: sizeClasses.control.fontSize,
                                gridArea: '1 / 1 / 2 / 3'
                            }),
                            singleValue: (base, state) => ({
                                ...base,
                                fontSize: sizeClasses.control.fontSize,
                                gridArea: '1 / 1 / 2 / 3',
                                ...(state.isDisabled ? { color: '#111827' } : {})
                            })
                        }, isSearchable: isSearchable, isClearable: isClearable, isMulti: isMulti, isDisabled: disabled, closeMenuOnSelect: isMulti ? false : true, hideSelectedOptions: false, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false) }), helperText && (_jsx("p", { className: `mt-1 text-gray-600 dark:text-gray-400 ${sizeClasses.helper}`, children: helperText })), error && (_jsx("p", { className: `mt-1 text-red-600 dark:text-red-400 ${sizeClasses.helper}`, children: error }))] })] }));
}
export function AdaptiveFormSection({ title, description, children, className = '', collapsible = false, defaultExpanded = true }) {
    const context = useContext(AdaptiveFormContext);
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    if (!context) {
        throw new Error('AdaptiveFormSection must be used within an AdaptiveForm');
    }
    const { density } = context;
    const getSectionClasses = () => {
        return density === 'compact' ? 'mb-3' : 'mb-6';
    };
    const getTitleClasses = () => {
        return density === 'compact'
            ? 'text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-2 pb-1 border-b border-gray-200 dark:border-gray-700'
            : 'text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-3 pb-2 border-b border-gray-200 dark:border-gray-700';
    };
    return (_jsxs("div", { className: cn(getSectionClasses(), className), children: [_jsxs("div", { className: cn(getTitleClasses(), collapsible && 'cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 flex items-center justify-between'), onClick: collapsible ? () => setIsExpanded(!isExpanded) : undefined, children: [_jsxs("div", { children: [title, description && (_jsx("p", { className: `mt-1 font-normal text-gray-600 dark:text-gray-400 normal-case tracking-normal ${density === 'compact' ? 'text-xs' : 'text-sm'}`, children: description }))] }), collapsible && (_jsx("svg", { className: cn('w-4 h-4 transition-transform duration-200', isExpanded && 'rotate-180'), fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" }) }))] }), (!collapsible || isExpanded) && (_jsx("div", { className: density === 'compact' ? 'space-y-0' : 'space-y-2', children: children }))] }));
}
// KeyboardHints component for showing navigation shortcuts
export function AdaptiveFormKeyboardHints({ className = '' }) {
    const context = useContext(AdaptiveFormContext);
    if (!context)
        return null;
    const { layout, density } = context;
    const getHints = () => {
        switch (layout) {
            case 'horizontal':
            case 'vertical':
                return [
                    { key: '↵ Enter', action: 'Next Field' },
                    { key: '↑↓', action: 'Navigate' }
                ];
            case 'inline':
                return [
                    { key: '→←', action: 'Navigate' },
                    { key: '↵ Enter', action: 'Next Field' }
                ];
            default:
                return [
                    { key: '↵ Enter', action: 'Next Field' }
                ];
        }
    };
    return (_jsx("div", { className: cn(`flex items-center gap-4 text-gray-500 dark:text-gray-400 ${density === 'compact' ? 'text-xs' : 'text-sm'}`, className), children: getHints().map((hint, index) => (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "font-semibold", children: hint.key }), _jsx("span", { children: "=" }), _jsx("span", { children: hint.action })] }, index))) }));
}
